import { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const CheckoutModal = ({ plan, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', clinic_name: '' });
  const [formValid, setFormValid] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const paypalRef = useRef(null);
  const paypalButtonsRendered = useRef(false);

  const update = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    setFormValid(newForm.name && newForm.email && newForm.phone && newForm.clinic_name);
  };

  const months = plan.commitmentMonths || 1;
  const subscriptionTotal = plan.monthlyAmount * months;
  const setupTotal = plan.setupFeeAmount || 0;
  const grandTotal = subscriptionTotal + setupTotal;
  const totalFormatted = `$${grandTotal.toLocaleString()}`;

  useEffect(() => {
    if (document.getElementById('paypal-sdk')) {
      setPaypalLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
    script.async = true;
    script.onload = () => setPaypalLoaded(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!paypalLoaded || !formValid || !paypalRef.current || paypalButtonsRendered.current) return;
    if (!window.paypal) return;

    paypalButtonsRendered.current = true;
    paypalRef.current.innerHTML = '';

    const totalAmount = grandTotal;

    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'black',
        shape: 'pill',
        label: 'pay',
        height: 45,
      },
      createOrder: (data, actions) => {
        const items = [
          {
            name: `${plan.name} Plan - ${months} Month${months > 1 ? 's' : ''}`,
            unit_amount: { currency_code: 'USD', value: String(subscriptionTotal) },
            quantity: '1',
          },
        ];
        if (plan.setupFee) {
          items.push({
            name: `${plan.name} Plan - One-Time Setup Fee`,
            unit_amount: { currency_code: 'USD', value: String(setupTotal) },
            quantity: '1',
          });
        }
        return actions.order.create({
          purchase_units: [{
            description: `Emplozera Studio - ${plan.name} Plan`,
            amount: {
              currency_code: 'USD',
              value: String(totalAmount),
              breakdown: {
                item_total: { currency_code: 'USD', value: String(totalAmount) },
              },
            },
            items,
          }],
        });
      },
      onApprove: async (data, actions) => {
        setProcessing(true);
        try {
          const details = await actions.order.capture();
          await axios.post(`${BACKEND_URL}/api/orders`, {
            paypal_order_id: details.id,
            plan: plan.name,
            amount: grandTotal,
            setup_fee: setupTotal,
            monthly: plan.monthlyAmount,
            commitment_months: months,
            customer_name: form.name,
            customer_email: form.email,
            customer_phone: form.phone,
            clinic_name: form.clinic_name,
            status: details.status,
          });
          toast.success(`Payment successful! Welcome to the ${plan.name} plan.`);
          onClose();
        } catch {
          toast.error('Payment captured but failed to save order. Please contact support.');
        } finally {
          setProcessing(false);
        }
      },
      onError: () => {
        toast.error('Payment failed. Please try again.');
        setProcessing(false);
      },
    }).render(paypalRef.current);
  }, [paypalLoaded, formValid, plan, form]);

  // Reset paypal buttons when form validity changes
  useEffect(() => {
    if (!formValid) {
      paypalButtonsRendered.current = false;
    }
  }, [formValid]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="checkout-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-[#D6CEBF]">
          <div>
            <h3 className="font-heading text-xl font-semibold text-[#11181C]">Subscribe to {plan.name}</h3>
            <p className="text-sm text-[#4A5568] mt-1">
              {plan.usd}/mo x {months} months
              {plan.setupFee && <span> + ${setupTotal} setup</span>}
              {' '}= {totalFormatted}
            </p>
          </div>
          <button onClick={onClose} className="text-[#4A5568] hover:text-[#11181C] transition-colors" data-testid="checkout-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-[#EDE7D9] rounded-xl p-4">
            <p className="text-xs font-bold text-[#1B3F33] uppercase tracking-wider mb-2">Payment Summary</p>
            <div className="flex justify-between text-sm text-[#4A5568]">
              <span>{plan.name} Plan ({months} month{months > 1 ? 's' : ''} x ${plan.monthlyAmount})</span>
              <span>${subscriptionTotal.toLocaleString()}</span>
            </div>
            {plan.setupFee && (
              <div className="flex justify-between text-sm text-[#4A5568] mt-1">
                <span>One-time setup fee</span>
                <span>${setupTotal}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold text-[#1B3F33] mt-2 pt-2 border-t border-[#D6CEBF]">
              <span>Total due today</span>
              <span>{totalFormatted}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-1 block">Full Name *</Label>
              <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Dr. John Doe" className="form-input-underline" data-testid="checkout-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-1 block">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="doctor@clinic.com" className="form-input-underline" data-testid="checkout-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-1 block">Phone *</Label>
              <Input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="Your phone number" className="form-input-underline" data-testid="checkout-phone" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#4A5568] font-semibold mb-1 block">Clinic Name *</Label>
              <Input value={form.clinic_name} onChange={(e) => update('clinic_name', e.target.value)} placeholder="Your Clinic Name" className="form-input-underline" data-testid="checkout-clinic" />
            </div>
          </div>

          {!formValid && (
            <p className="text-xs text-[#8C6330] text-center">Please fill in all fields to proceed with payment</p>
          )}

          {formValid && (
            <div ref={paypalRef} data-testid="paypal-buttons">
              {!paypalLoaded && <p className="text-sm text-[#4A5568] text-center py-4">Loading payment options...</p>}
            </div>
          )}

          {processing && (
            <p className="text-sm text-[#1B3F33] text-center font-medium py-2">Processing your payment...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
